import { EventsEnum } from '@hubxtech/hubx-client-analytics-package/lib/src/enum/deepsearch/eventsEnum';
import cn from 'clsx';
import {
  type ChangeEvent,
  type KeyboardEvent,
  type ReactElement,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useIsMobile } from '@/hooks/is-mobile';
import { logHubxEvent } from '@/lib/analytics/services/log-hubx-event';
import { Button } from '@/uikit/components';
import { cdnUrl } from '@/utils/cdn-urls';

import styles from './Hero.module.scss';

interface HeroProps {
  searchText: string | null;
  setSearchText: (text: string | null) => void;
  handleSearch: () => void;
}

const trustedAvatars = [
  `${cdnUrl}/share/profile-1.webp`,
  `${cdnUrl}/share/profile-2.webp`,
  `${cdnUrl}/share/profile-3.webp`,
] as const;

export function Hero({
  searchText,
  setSearchText,
  handleSearch,
}: HeroProps): ReactElement {
  const isMobile = useIsMobile(768);
  const { t } = useTranslation();
  const backgroundImageSrc = isMobile
    ? `${cdnUrl}/landing5/welcome-bg-mobile.webp`
    : `${cdnUrl}/landing5/welcome-bg.webp`;
  const [loadedBackgroundSrc, setLoadedBackgroundSrc] = useState<string | null>(
    null,
  );
  const isBackgroundLoaded = loadedBackgroundSrc === backgroundImageSrc;

  const onChangeSearchText = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchText(event.target.value);
  };

  const onClickSearchButton = (): void => {
    const trimmedSearchText = searchText?.trim() ?? '';
    if (trimmedSearchText === '') return;

    setSearchText(trimmedSearchText);

    logHubxEvent({
      eventName: EventsEnum.BTN_LANDING_SEARCH,
      event_params: {
        query: trimmedSearchText,
      },
    });

    handleSearch();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    onClickSearchButton();
  };

  const onBackgroundImageReady = (): void => {
    setLoadedBackgroundSrc(backgroundImageSrc);
  };

  return (
    <section className={styles.hero}>
      <div className={styles.hero__inner}>
        <img
          src={backgroundImageSrc}
          alt=""
          className={styles.hero__background}
          draggable="false"
          loading="eager"
          onLoad={onBackgroundImageReady}
          onError={onBackgroundImageReady}
        />
        <div
          className={cn(styles.hero__content, {
            [styles['hero__content--loaded']]: isBackgroundLoaded,
          })}
          aria-hidden={!isBackgroundLoaded}
        >
          <div className={styles.hero__copy}>
            <div className={styles.hero__eyebrow}>
              <h1 className={styles.hero__title}>
                <Trans
                  i18nKey="landing5.hero.title"
                  components={{ span: <span /> }}
                >
                  Find anyone online with <span>Deepsearch</span>
                </Trans>
              </h1>
              <p className={styles.hero__subtitle}>
                <Trans i18nKey="landing5.hero.subtitle">
                  Find people by name, number or social link. Access their
                  social profiles in seconds.
                </Trans>
              </p>
            </div>

            <div className={styles.hero__search}>
              <input
                type="text"
                className={styles.hero__input}
                onChange={onChangeSearchText}
                onKeyDown={onKeyDown}
                placeholder={t('landing5.hero.searchPlaceholder')}
                autoComplete="off"
                value={searchText ?? ''}
              />
              <Button
                className={styles.hero__button}
                variant="primary"
                onClick={onClickSearchButton}
                disabled={searchText === null || searchText.trim() === ''}
              >
                <Trans i18nKey="landing5.hero.searchButton">Search Now</Trans>
              </Button>
            </div>

            <div className={styles.hero__trust}>
              <div className={styles['hero__trust-avatars']} aria-hidden="true">
                {trustedAvatars.map((avatar, index) => (
                  <img
                    key={avatar}
                    src={avatar}
                    alt=""
                    className={styles['hero__trust-avatar']}
                    style={{ zIndex: trustedAvatars.length - index }}
                  />
                ))}
              </div>
              <p className={styles['hero__trust-text']}>
                <span>
                  <Trans i18nKey="landing5.hero.trustedBy">
                    Trusted by 1m+ professionals
                  </Trans>
                </span>
                <span>
                  <Trans i18nKey="landing5.hero.peopleSearched">
                    23m+ people searched
                  </Trans>
                </span>
              </p>
            </div>
          </div>

          <div className={styles.hero__visual} aria-hidden="true">
            <img
              src={`${cdnUrl}/landing5/welcome.webp`}
              alt=""
              className={styles['hero__right-image']}
              draggable="false"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
